import { z } from 'zod';

// src/primitives.ts
var isoTimestamp = z.string().datetime({ offset: true });
var id = z.string().min(1);
var semver = z.string().regex(
  /^\d+\.\d+\.\d+(?:-[0-9A-Za-z-.]+)?(?:\+[0-9A-Za-z-.]+)?$/,
  "must be a semantic version, e.g. 1.4.2"
);
var embedding = z.array(z.number());
var assetRole = z.union([
  z.enum(["character", "prop", "background"]),
  z.string()
]);
var detectedAsset = z.object({
  label: z.string(),
  /** [y_min, x_min, y_max, x_max], each 0-1000 (normalized). */
  box_2d: z.array(z.number()),
  asset_role: assetRole
});
var storyIllustration = z.object({
  url: z.string().optional(),
  prompt: z.string(),
  data: z.string().optional(),
  status: z.string().optional(),
  focus: z.string().optional(),
  shot_type: z.string().optional(),
  subjects: z.array(z.string()).optional(),
  settings: z.array(z.string()).optional(),
  props: z.array(z.string()).optional(),
  appearance_changes: z.record(z.string(), z.array(z.string())).optional(),
  detected_assets: z.array(detectedAsset).optional()
});

// src/collections.ts
var COLLECTIONS = {
  /** Tenants / licensees. */
  orgs: "orgs",
  /** Top-level universes; each carries `owner_id` = its org id. */
  universes: "universes",
  /** Raw ingestion material (internal; never exposed to portal clients). */
  ingestionSources: "ingestion_sources",
  /** SSUP user mirror docs. */
  users: "users",
  /** SSUP admin allowlist (`admins/{email}`). */
  admins: "admins",
  /**
   * Partner validation approvals — a SUBCOLLECTION under a universe
   * (`universes/{universeId}/approvals/{id}`), so rules inherit the parent
   * universe's `owner_id` gate.
   */
  approvals: "approvals"
};
var cameraAngle = z.enum(["low", "eye", "high"]);
var visionAnalysis = z.object({
  /** Structural description: positions, poses, scene layout. */
  description: z.string(),
  /**
   * Actually observed shot type — may differ from the LLM-requested value.
   * Canonical values: "wide" | "medium" | "close" | "detail".
   */
  shot_type: z.string().optional(),
  /**
   * Actually observed compositional focus.
   * Canonical values: "character_solo" | "character_interaction" |
   * "environment" | "detail".
   */
  focus: z.string().optional(),
  camera_angle: cameraAngle.optional(),
  subjects_visible: z.array(z.string()).optional(),
  settings_visible: z.array(z.string()).optional(),
  props_visible: z.array(z.string()).optional(),
  detected_assets: z.array(detectedAsset).optional()
});
var timestamp = z.union([
  z.object({ seconds: z.number(), nanoseconds: z.number().optional() }),
  z.object({ _seconds: z.number(), _nanoseconds: z.number().optional() }),
  z.string(),
  z.number()
]);
var StoryIdeaType = /* @__PURE__ */ ((StoryIdeaType2) => {
  StoryIdeaType2["CONCEPT"] = "concept";
  StoryIdeaType2["CHARACTER"] = "character";
  StoryIdeaType2["CHARACTER_NAME"] = "character_name";
  StoryIdeaType2["CHARACTER_ILLUSTRATION"] = "character_illustration";
  StoryIdeaType2["SETTING"] = "setting";
  StoryIdeaType2["SETTING_ILLUSTRATION"] = "setting_illustration";
  return StoryIdeaType2;
})(StoryIdeaType || {});
var StoryPhaseType = /* @__PURE__ */ ((StoryPhaseType2) => {
  StoryPhaseType2["INTRODUCTION"] = "introduction";
  StoryPhaseType2["INTRODUCTION_ACTIVITY"] = "introduction_activity";
  StoryPhaseType2["INTRODUCTION_MOTIVATION"] = "introduction_motivation";
  StoryPhaseType2["INTRODUCTION_CUSTOM"] = "introduction_custom";
  StoryPhaseType2["CONFLICT"] = "conflict";
  StoryPhaseType2["CONFLICT_REACTION"] = "conflict_reaction";
  StoryPhaseType2["CONFLICT_STAKES"] = "conflict_stakes";
  StoryPhaseType2["CONFLICT_CUSTOM"] = "conflict_custom";
  StoryPhaseType2["RESOLUTION"] = "resolution";
  StoryPhaseType2["RESOLUTION_RESULT"] = "resolution_result";
  StoryPhaseType2["RESOLUTION_CUSTOM"] = "resolution_custom";
  StoryPhaseType2["CONCLUSION"] = "conclusion";
  StoryPhaseType2["CONCLUSION_FEELING"] = "conclusion_feeling";
  StoryPhaseType2["CONCLUSION_FUTURE"] = "conclusion_future";
  StoryPhaseType2["CONCLUSION_CUSTOM"] = "conclusion_custom";
  return StoryPhaseType2;
})(StoryPhaseType || {});
var StoryWritingType = /* @__PURE__ */ ((StoryWritingType2) => {
  StoryWritingType2["PAGE_INTRODUCTION"] = "page_introduction";
  StoryWritingType2["PAGE_TEXT"] = "page_text";
  StoryWritingType2["PAGE_REVIEW"] = "page_review";
  StoryWritingType2["PAGE_ILLUSTRATION"] = "page_illustration";
  StoryWritingType2["PAGE_NEXT"] = "page_next";
  return StoryWritingType2;
})(StoryWritingType || {});
var StoryCompletionType = /* @__PURE__ */ ((StoryCompletionType2) => {
  StoryCompletionType2["BOOK_TITLE"] = "book_title";
  StoryCompletionType2["BOOK_SUMMARY"] = "book_summary";
  StoryCompletionType2["BOOK_TRIAL_SUMMARY"] = "book_trial_summary";
  return StoryCompletionType2;
})(StoryCompletionType || {});
var StoryBlurbType = /* @__PURE__ */ ((StoryBlurbType2) => {
  StoryBlurbType2["BLURB"] = "blurb";
  StoryBlurbType2["WIP_BLURB"] = "wip_blurb";
  return StoryBlurbType2;
})(StoryBlurbType || {});
var worldContextRule = z.object({
  rule: z.string()
});
var storyIdea = z.object({
  ["concept" /* CONCEPT */]: z.string(),
  ["character" /* CHARACTER */]: z.string(),
  ["character_name" /* CHARACTER_NAME */]: z.string().optional(),
  ["setting" /* SETTING */]: z.string(),
  ["character_illustration" /* CHARACTER_ILLUSTRATION */]: storyIllustration,
  ["setting_illustration" /* SETTING_ILLUSTRATION */]: storyIllustration
});
var storyWorld = z.object({
  /** secondary characters, props, vehicles with visual identity */
  secondary_illustrations: z.array(storyIllustration).optional(),
  /** lore rules for the illustration agent (conditional or always-on) */
  context: z.array(worldContextRule).optional()
});
var storyPage = z.object({
  phase: z.enum(StoryPhaseType),
  text: z.string(),
  illustration: storyIllustration
});
var storyPageStep = z.object({
  type: z.enum(StoryWritingType),
  phase: z.enum(StoryPhaseType),
  index: z.number()
});
var storyPhase = z.record(z.string(), z.string());
var storyState = z.object({
  id: z.string(),
  idea: storyIdea,
  world: storyWorld.optional(),
  writing: z.array(storyPage),
  events: storyPhase,
  title: z.string(),
  created_at: timestamp,
  last_updated_at: timestamp,
  user_id: z.string(),
  profile_id: z.string(),
  is_public: z.boolean(),
  /** Simple list of emails this story has been shared with. */
  shared_emails: z.array(z.string()).optional(),
  cover: storyIllustration.optional(),
  thumbnail: storyIllustration.optional(),
  blurb: z.string().optional(),
  blurb_updated_at: timestamp.optional(),
  from_template: z.boolean().optional(),
  template_id: z.string().optional(),
  status: z.enum(["wip", "completed"]),
  short_id: z.string().optional()
});
var minimalStoryState = storyState.pick({
  id: true,
  idea: true,
  events: true,
  writing: true,
  title: true,
  user_id: true,
  profile_id: true,
  is_public: true,
  created_at: true,
  last_updated_at: true,
  template_id: true,
  shared_emails: true,
  cover: true,
  thumbnail: true,
  blurb: true,
  blurb_updated_at: true,
  short_id: true
}).extend({
  idea: storyIdea.optional(),
  events: storyPhase.optional(),
  created_at: timestamp.optional(),
  last_updated_at: timestamp.optional()
});
var knowledge = z.object({
  base: z.string(),
  base_completion: z.string().optional(),
  base_writing: z.string().optional(),
  base_illustration: z.string().optional(),
  summarize_prompt: z.string().optional(),
  reference_selection: z.string().optional(),
  character: z.string(),
  steps: z.record(z.string(), z.string())
});
var ssupRole = z.enum(["admin", "partner"]);
var ssupClaims = z.object({
  role: ssupRole,
  /** The partner's org. Present for `partner`; absent for `admin`. */
  org_id: id.optional()
}).superRefine((value, ctx) => {
  if (value.role === "partner" && !value.org_id) {
    ctx.addIssue({
      code: "custom",
      message: "partner claims must include an org_id",
      path: ["org_id"]
    });
  }
});
var org = z.object({
  id,
  name: z.string(),
  created_at: isoTimestamp,
  last_updated_at: isoTimestamp.optional()
});
var ssupAdmin = z.object({
  email: z.email(),
  added_at: isoTimestamp,
  /** The admin who granted this access (from the console operator's identity). */
  added_by: z.email().optional(),
  display_name: z.string().optional()
});
var ssupUser = z.object({
  uid: id,
  email: z.email(),
  display_name: z.string().optional(),
  role: ssupRole,
  /** The user's org. Present for `partner`; absent for `admin`. */
  org_id: id.optional(),
  created_at: isoTimestamp,
  last_updated_at: isoTimestamp.optional()
});
var approvalVerdict = z.enum(["approved", "rejected", "changes_requested"]);
var partnerApproval = z.object({
  id,
  /** The universe this approval belongs to (matches the parent doc id). */
  universe_id: id,
  /** The exact UDS version being approved. */
  uds_version: semver,
  verdict: approvalVerdict,
  /** Optional free-text note (rationale, requested changes). */
  note: z.string().optional(),
  /** Firebase Auth uid of the approving partner (authoritative from the JWT). */
  approver_uid: id,
  created_at: isoTimestamp
});
var entityKind = z.enum(["character", "setting", "prop"]);
var variantKind = z.enum(["outfit", "life_stage", "form", "other"]);
var entityVariant = z.object({
  id,
  kind: variantKind,
  label: z.string(),
  reference_asset_ids: z.array(id).default([]),
  /** Per-variant identity centroid (used when outfit affects perceived identity). */
  identity_centroid: embedding.optional()
});
var canonicalEntity = z.object({
  id,
  kind: entityKind,
  name: z.string(),
  /** Alternate names used during Phase-1 canonical entity resolution. */
  aliases: z.array(z.string()).default([]),
  canonical_reference_asset_ids: z.array(id).default([]),
  variants: z.array(entityVariant).default([]),
  /** Entity-level identity centroid (used when identity is stable across variants). */
  identity_centroid: embedding.optional()
});
var appearanceVariantMapping = z.object({
  entity_id: id,
  /** The `appearance_changes` string emitted by the existing engine. */
  appearance_change: z.string(),
  variant_id: id
});
var constraintLayerType = z.enum(["CCL", "WLL", "NSL", "VSL"]);
var phaseTag = z.string();
var constraintRule = z.object({
  id,
  description: z.string().optional(),
  phase_tags: z.array(phaseTag).default([]),
  // --- Tier-1 deterministic gate inputs (sub-millisecond checks) ---
  banned_terms: z.array(z.string()).default([]),
  forbidden_actions: z.array(z.string()).default([]),
  forbidden_themes: z.array(z.string()).default([]),
  required_vocabulary: z.array(z.string()).default([]),
  // --- Tier-2 semantic judge input ---
  /** Free-form canon guidance handed to the LLM-as-judge alongside RAG hits. */
  canon_notes: z.string().optional()
});
var constraintLayer = z.object({
  type: constraintLayerType,
  rules: z.array(constraintRule).default([])
});
var referenceAssetKind = z.enum([
  "character",
  "setting",
  "prop",
  "style_guide"
]);
var referenceSource = z.enum(["canonical", "approved_generated"]);
var ipHolderApproval = z.object({
  approved: z.boolean(),
  ip_holder: z.string().optional(),
  approved_at: isoTimestamp.optional(),
  notes: z.string().optional()
});
var referenceAsset = z.object({
  id,
  entity_id: id,
  kind: referenceAssetKind,
  /** Optional variant this asset depicts (outfit/life-stage/form). */
  variant_id: id.optional(),
  /** Immutable, CDN-fronted Cloud Storage path to the binary. */
  storage_path: z.string(),
  // --- Filter keys (narrow candidates before ranking) ---
  view: z.string().optional(),
  expression: z.string().optional(),
  lighting: z.string().optional(),
  tags: z.array(z.string()).default([]),
  // --- Provenance / permissions ---
  source: referenceSource,
  approval: ipHolderApproval,
  usage_scope: z.array(z.string()).default([]),
  // --- Precomputed embeddings (ingest-time) ---
  appearance_embedding: embedding.optional(),
  style_embedding: embedding.optional()
});
var outputOwnership = z.enum(["platform", "licensor", "shared"]);
var rightsGrant = z.object({
  /** Canonical entity this grant applies to (character/world/voice/likeness). */
  entity_id: id,
  permitted_uses: z.array(z.string()).default([]),
  /** Permitted distribution channels (e.g. "print", "app", "streaming"). */
  channels: z.array(z.string()).default([]),
  /** Permitted territories (e.g. ISO country codes or region labels). */
  territories: z.array(z.string()).default([]),
  /** Explicit carve-outs that override permitted_uses/channels/territories. */
  exclusions: z.array(z.string()).default([]),
  /** Required field per doc B2: ownership disposition of derived AI outputs. */
  output_ownership: outputOwnership,
  valid_from: isoTimestamp.optional(),
  valid_until: isoTimestamp.optional()
});
var rights = z.object({
  grants: z.array(rightsGrant).default([])
});

// src/ingestion.ts
var ingestionSourceKind = z.enum(["pdf", "image", "text", "docx", "url", "other"]);
var ingestionSourceStatus = z.enum(["uploaded", "normalized", "extracted", "failed"]);
var reviewPolicy = z.enum(["review_all", "review_below_threshold", "auto_accept"]);
var stagingMode = z.enum(["staged", "direct"]);
var changeOp = z.enum(["add", "update", "conflict"]);
var candidateTarget = z.enum([
  "entity",
  "constraint",
  "reference",
  "style_guide",
  "rights"
]);
var reviewState = z.enum(["pending", "approved", "rejected"]);
var ingestionRunStatus = z.enum(["running", "awaiting_review", "applied", "failed"]);
var sourceLocator = z.object({
  source_id: id,
  page: z.number().int().min(0).optional(),
  char_start: z.number().int().min(0).optional(),
  char_end: z.number().int().min(0).optional(),
  /** [y_min, x_min, y_max, x_max], normalized 0-1000 (as per detectedAsset). */
  bbox: z.array(z.number()).optional()
});
var ingestionSource = z.object({
  id,
  universe_id: id,
  kind: ingestionSourceKind,
  mime: z.string().optional(),
  filename: z.string().optional(),
  /** Immutable Cloud Storage path to the binary (pdf/image/docx). */
  storage_path: z.string().optional(),
  /** Inline content for `text`/`url` kinds. */
  text: z.string().optional(),
  content_hash: z.string(),
  byte_size: z.number().int().min(0).optional(),
  uploaded_by: z.string(),
  uploaded_at: isoTimestamp,
  status: ingestionSourceStatus.default("uploaded")
});
var ingestionConfig = z.object({
  review_policy: reviewPolicy.default("review_all"),
  /** Adds at/above this confidence may auto-accept under `review_below_threshold`. */
  confidence_threshold: z.number().min(0).max(1).default(0.8),
  staging_mode: stagingMode.default("staged")
});
var ingestionRunStats = z.object({
  candidates: z.number().int().min(0).default(0),
  adds: z.number().int().min(0).default(0),
  updates: z.number().int().min(0).default(0),
  conflicts: z.number().int().min(0).default(0),
  approved: z.number().int().min(0).default(0),
  rejected: z.number().int().min(0).default(0)
});
var ingestionRun = z.object({
  id,
  universe_id: id,
  /** The draft UDS version this run's approved candidates promote into. */
  uds_version_id: id,
  source_ids: z.array(id).default([]),
  config: ingestionConfig,
  status: ingestionRunStatus.default("running"),
  created_by: z.string(),
  created_at: isoTimestamp,
  applied_at: isoTimestamp.optional(),
  stats: ingestionRunStats.optional()
});
var candidateBase = z.object({
  id,
  run_id: id,
  universe_id: id,
  op: changeOp,
  /** Extractor confidence, 0-1. Absent for deterministic/structured extraction. */
  confidence: z.number().min(0).max(1).optional(),
  locators: z.array(sourceLocator).default([]),
  review_state: reviewState.default("pending"),
  /** For `update`: the existing item this candidate revises. */
  supersedes_id: id.optional(),
  /** For `conflict`: the existing item this candidate contradicts. */
  conflict_with_id: id.optional(),
  reviewed_by: z.string().optional(),
  reviewed_at: isoTimestamp.optional(),
  note: z.string().optional()
});
var ingestionCandidate = z.discriminatedUnion("target", [
  candidateBase.extend({
    target: z.literal("entity"),
    entity: canonicalEntity
  }),
  candidateBase.extend({
    target: z.literal("constraint"),
    layer_type: constraintLayerType,
    rule: constraintRule
  }),
  candidateBase.extend({
    target: z.literal("reference"),
    asset: referenceAsset,
    vision: visionAnalysis.optional()
  }),
  candidateBase.extend({
    target: z.literal("style_guide"),
    asset: referenceAsset
  }),
  candidateBase.extend({
    target: z.literal("rights"),
    rights
  })
]);

// src/universe.ts
var universeRule = z.object({
  rule: z.string()
});
var universeStatus = z.enum(["onboarding", "active", "paused", "archived"]);
var universe = z.object({
  id,
  name: z.string(),
  owner_id: id,
  /** Licence/relationship status. See {@link universeStatus}. */
  status: universeStatus.default("onboarding"),
  created_at: isoTimestamp,
  last_updated_at: isoTimestamp,
  rules: z.array(universeRule).default([]),
  is_public: z.boolean().default(false),
  /**
   * The currently certified UDS version stories should pin against. Production
   * generation is blocked unless a matching active certification exists (see
   * certification.ts).
   */
  active_uds_version: semver.optional(),
  /**
   * Per-universe default ingestion behaviour (review policy, confidence
   * threshold, staging mode). A run copies/overrides this. See ingestion.ts.
   */
  ingestion_config: ingestionConfig.optional()
});
var udsStatus = z.enum([
  "draft",
  "internal_validation",
  "partner_validation",
  "certified",
  "deprecated"
]);
var universeDefinitionSchema = z.object({
  id,
  universe_id: id,
  /** Immutable once `status` is "certified". */
  version: semver,
  status: udsStatus.default("draft"),
  created_at: isoTimestamp,
  entities: z.array(canonicalEntity).default([]),
  constraint_layers: z.array(constraintLayer).default([]),
  appearance_variant_mappings: z.array(appearanceVariantMapping).default([]),
  rights
});
var udsVersionRef = z.object({
  universe_id: id,
  version: semver
});
var severity = z.enum(["info", "warning", "error", "critical"]);
var conformanceTier = z.enum(["tier_1", "tier_2"]);
var violationSpan = z.object({
  start: z.number().int().min(0),
  end: z.number().int().min(0)
});
var conformanceViolation = z.object({
  entity_id: id.optional(),
  rule_id: id,
  severity,
  span: violationSpan.optional(),
  message: z.string().optional(),
  suggested_fix: z.string().optional()
});
var conformanceReport = z.object({
  tier: conformanceTier,
  passed: z.boolean(),
  violations: z.array(conformanceViolation).default([]),
  /** Optional identifier of the judge model/tier used (Tier-2). */
  judge_model: z.string().optional()
});
var fluxPromptSpec = z.object({
  /** JSON-prompting payload passed to the image API. */
  json_prompt: z.record(z.string(), z.unknown()).optional(),
  /** Hex-palette control (e.g. ["#1a2b3c", ...]). */
  hex_palette: z.array(z.string()).default([]),
  /** Reference assets to condition on; API ceiling is ~10 (target budget ~7). */
  reference_asset_ids: z.array(id).max(10).default([])
});
var selectedReference = z.object({
  asset_id: id,
  weight: z.number().optional()
});
var referenceSelection = z.object({
  subject_entity_id: id,
  variant_id: id.optional(),
  selected: z.array(selectedReference).default([])
});
var verificationMethod = z.enum(["embedding", "vlm"]);
var identityResult = z.object({
  entity_id: id,
  variant_id: id.optional(),
  method: verificationMethod,
  /** Cosine similarity to the canonical centroid (embedding method). */
  similarity: z.number().optional(),
  passed: z.boolean()
});
var verifiedAsset = detectedAsset.extend({
  entity_id: id.optional(),
  variant_id: id.optional(),
  identity: identityResult.optional()
});
var brandRiskFinding = z.object({
  category: z.string(),
  severity: severity.optional(),
  detail: z.string().optional()
});
var visualVerification = z.object({
  page_index: z.number().int().min(0).optional(),
  assets: z.array(verifiedAsset).default([]),
  /** Style-embedding adherence score (stage 4). */
  style_adherence: z.number().optional(),
  /** Safety results against the brand-risk taxonomy (stage 5). */
  safety_findings: z.array(brandRiskFinding).default([]),
  passed: z.boolean()
});
var conformancePassRecord = z.object({
  text_reports: z.array(conformanceReport).default([]),
  visual_reports: z.array(visualVerification).default([]),
  passed_at: isoTimestamp.optional()
});
var c2paIngredient = z.object({
  universe_id: id,
  uds_version: semver,
  reference_asset_ids: z.array(id).default([]),
  conformance_pass: conformancePassRecord.optional(),
  certification_config_id: id.optional()
});
var watermark = z.object({
  type: z.string(),
  id: z.string().optional()
});
var c2paManifest = z.object({
  artifact_id: id,
  ingredient: c2paIngredient,
  /** Cryptographic signature over the manifest. */
  signature: z.string().optional(),
  watermark: watermark.optional(),
  /** EU AI Act Art. 50 machine-readable AI-content marking present. */
  ai_content_marked: z.boolean().default(true)
});
var certificationRecord = z.object({
  id,
  universe_id: id,
  uds_version: semver,
  config_id: id.optional(),
  /** Hash of this record's contents. */
  hash: z.string(),
  /** Hash of the previous ledger entry (chain link); absent for the first. */
  prev_hash: z.string().optional(),
  active: z.boolean().default(false),
  signed_by: z.string().optional(),
  created_at: isoTimestamp
});
var killSwitchScope = z.enum(["universe", "entity"]);
var killSwitch = z.object({
  scope: killSwitchScope,
  target_id: id,
  active: z.boolean().default(false),
  reason: z.string().optional(),
  set_at: isoTimestamp.optional()
});
var takedownStatus = z.enum([
  "requested",
  "in_progress",
  "completed",
  "rejected"
]);
var takedownRequest = z.object({
  id,
  /** Artifact to recall, traceable via its C2PA manifest. */
  manifest_artifact_id: id,
  reason: z.string(),
  requested_by: z.string().optional(),
  status: takedownStatus.default("requested"),
  requested_at: isoTimestamp.optional()
});
var brandRiskCategory = z.enum([
  "violence",
  "sexualisation",
  "political_association",
  "competitor_association",
  "disparagement",
  "other"
]);
var adversarialProbe = z.object({
  id,
  category: brandRiskCategory,
  prompt: z.string(),
  /** What a conformant system should do (e.g. "refuse", "sanitise"). */
  expected_outcome: z.string().optional(),
  authored_by: z.string().optional()
});
var refinementCaps = z.object({
  max_text_retries: z.number().int().min(0).default(2),
  max_image_retries: z.number().int().min(0).default(2)
});
var refinementSpend = z.object({
  story_id: id,
  text_retries: z.number().int().min(0).default(0),
  image_retries: z.number().int().min(0).default(0),
  /** Accumulated generation cost attributable to refinement. */
  cost: z.number().optional(),
  /** Set when caps were exhausted and the page was routed to portal review. */
  routed_to_review: z.boolean().default(false)
});

export { COLLECTIONS, StoryBlurbType, StoryCompletionType, StoryIdeaType, StoryPhaseType, StoryWritingType, adversarialProbe, appearanceVariantMapping, approvalVerdict, assetRole, brandRiskCategory, brandRiskFinding, c2paIngredient, c2paManifest, cameraAngle, candidateTarget, canonicalEntity, certificationRecord, changeOp, conformancePassRecord, conformanceReport, conformanceTier, conformanceViolation, constraintLayer, constraintLayerType, constraintRule, detectedAsset, embedding, entityKind, entityVariant, fluxPromptSpec, id, identityResult, ingestionCandidate, ingestionConfig, ingestionRun, ingestionRunStats, ingestionRunStatus, ingestionSource, ingestionSourceKind, ingestionSourceStatus, ipHolderApproval, isoTimestamp, killSwitch, killSwitchScope, knowledge, minimalStoryState, org, outputOwnership, partnerApproval, phaseTag, referenceAsset, referenceAssetKind, referenceSelection, referenceSource, refinementCaps, refinementSpend, reviewPolicy, reviewState, rights, rightsGrant, selectedReference, semver, severity, sourceLocator, ssupAdmin, ssupClaims, ssupRole, ssupUser, stagingMode, storyIdea, storyIllustration, storyPage, storyPageStep, storyPhase, storyState, storyWorld, takedownRequest, takedownStatus, timestamp, udsStatus, udsVersionRef, universe, universeDefinitionSchema, universeRule, universeStatus, variantKind, verificationMethod, verifiedAsset, violationSpan, visionAnalysis, visualVerification, watermark, worldContextRule };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map